import React, { useState, useRef } from 'react'
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Switch,
    FormControlLabel,
    Grid,
    Card,
    CardContent,
    LinearProgress,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import 'dayjs/locale/tr'
import {
    CloudDownload,
    CloudUpload,
    Schedule,
    RestaurantMenu,
    Refresh,
    Send
} from '@mui/icons-material'
import * as XLSX from 'xlsx'
import { ConfirmationModal } from '../../../../components/ConfirmationModal'
import { useSendMealAllowanceMutation } from '../../services/services'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/store'
import { encrypt } from '../../../../utils/encrypt'
import { enqueueSnackbar } from 'notistack'

interface MealAllowanceData {
    description: string
    receiverCardNo: string
    currency: string
    value: number
    __rowNum__: number
}

interface ValidationResult {
    class: string
    text: string
}

interface LoadMealBalanceProps {
    onClose?: () => void
}

const LoadMealBalance = ({ onClose }: LoadMealBalanceProps = {}) => {
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState<MealAllowanceData[]>([])
    const [invalidRows, setInvalidRows] = useState<MealAllowanceData[]>([])
    const [validRows, setValidRows] = useState<MealAllowanceData[]>([])
    const [isShowSendMoneyButton, setIsShowSendMoneyButton] = useState(false)
    const [isScheduled, setIsScheduled] = useState(false)
    const [scheduledDate, setScheduledDate] = useState<Dayjs | null>(null)
    const [message, setMessage] = useState('')
    const [messageType, setMessageType] = useState<'success' | 'error' | 'warning' | 'info'>('info')
    const [showNoValidRowsWarning, setShowNoValidRowsWarning] = useState(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [isConfirmLoading, setIsConfirmLoading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Redux ve API hooks
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState)
    const [sendMealAllowance, { isLoading: apiLoading }] = useSendMealAllowanceMutation()

    const fileFormat = ['.xlsx', '.csv', '.xls', '.xlsm', '.xlsb', '.xlt']

    const downloadSampleFile = () => {
        const excelFilePath = '/assets/data/YemekKartıTopluParaYukle.xlsx'
        const a = document.createElement('a')
        a.href = excelFilePath
        a.download = 'YemekKartıTopluParaYukle.xlsx'
        a.click()
    }

    const getCurrency = (data: string): string => {
        switch (data) {
            case 'USD': return '2'
            case 'EUR': return '3'
            case 'TL': return '1'
            default: return '1'
        }
    }


    const formatCurrency = (value: string, currency: string): string => {
        if (value != null && currency != null) {
            const currencyCode = getProperCurrencyCode(currency)
            const formatter = new Intl.NumberFormat('tr-TR', {
                style: 'currency',
                currency: currencyCode,
                minimumFractionDigits: 2,
            })
            return formatter.format(parseFloat(value))
        }
        return ''
    }

    const getProperCurrencyCode = (currency: string): string => {
        switch (currency) {
            case '1':
            case 'TL':
                return 'TRY'
            case '2':
            case 'USD':
                return 'USD'
            case '3':
            case 'EUR':
                return 'EUR'
            default:
                return 'TRY'
        }
    }

    const getCellClassAndText = (value: any, type: string): ValidationResult => {
        if (type === 'receiverCardNo' || type === 'currency') {
            return value
                ? { class: 'text-green-800 font-bold', text: value }
                : { class: 'text-red-800 font-bold', text: 'Eksik' }
        }

        if (type === 'value') {
            if (typeof value === 'string') {
                return { class: 'text-red-800 font-bold', text: 'Hatalı' }
            }
            return value
                ? { class: 'text-green-800 font-bold', text: value }
                : { class: 'text-red-800 font-bold', text: 'Eksik' }
        }

        return { class: '', text: '' }
    }

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) {
            setLoading(false)
            return
        }

        setLoading(true)
        const selectedFile = event.target.files[0]

        if (fileFormat.some((format) => selectedFile.name.endsWith(format))) {
            const fileReader = new FileReader()
            fileReader.onload = async (e: any) => {
                const binaryData = e.target.result
                const workbook = XLSX.read(binaryData, { type: 'binary' })
                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]
                if (worksheet['!ref']) {
                    worksheet['!ref'] = worksheet['!ref'].replace('A1', 'A2')
                }
                const excelData: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: null })

                const mappedData = excelData.map((row, index) => ({
                    description: row['Açıklama'],
                    receiverCardNo: row['Alıcı Kart No'],
                    currency: row['Para Birimi'],
                    value: row['Tutar'],
                    __rowNum__: index + 3
                }))

                const validRowsTemp: MealAllowanceData[] = []
                const invalidRowsTemp: MealAllowanceData[] = []

                for (let i = 0; i < mappedData.length; i++) {
                    const row = mappedData[i]

                    // Daha detaylı ve tutarlı validasyon
                    let isRowValid = true
                    let validationErrors = []

                    // Kart numarası validasyonu
                    if (!row.receiverCardNo ||
                        row.receiverCardNo === null ||
                        row.receiverCardNo === undefined ||
                        String(row.receiverCardNo).trim().length === 0) {
                        isRowValid = false
                        validationErrors.push('Kart numarası eksik')
                    }

                    // Para birimi validasyonu
                    if (!row.currency ||
                        row.currency === null ||
                        row.currency === undefined ||
                        !['TL', 'USD', 'EUR'].includes(String(row.currency).trim())) {
                        isRowValid = false
                        validationErrors.push('Para birimi geçersiz')
                    }

                    // Tutar validasyonu
                    const numericValue = Number(row.value)
                    if (row.value === null ||
                        row.value === undefined ||
                        row.value === '' ||
                        isNaN(numericValue) ||
                        numericValue <= 0) {
                        isRowValid = false
                        validationErrors.push('Tutar geçersiz')
                    }

                    if (isRowValid) {
                        validRowsTemp.push(row)
                    } else {
                        invalidRowsTemp.push(row)
                    }
                }

                setValidRows(validRowsTemp)
                setInvalidRows(invalidRowsTemp)
                setFormData(mappedData)
                setIsShowSendMoneyButton(validRowsTemp.length > 0)
                setLoading(false)

                // Mesaj gösterme mantığını düzelt - Sadece temel bilgi mesajı, detaylı uyarılar aşağıda gösterilecek
                if (validRowsTemp.length > 0 && invalidRowsTemp.length > 0) {
                    setMessage(`Excel dosyası yüklendi. Doğru satırlar: ${validRowsTemp.length}, Hatalı satırlar: ${invalidRowsTemp.length}`)
                    setMessageType('info')
                } else if (validRowsTemp.length > 0 && invalidRowsTemp.length === 0) {
                    setMessage(`Excel dosyası başarıyla yüklendi. Toplam ${validRowsTemp.length} satır işlenecek.`)
                    setMessageType('success')
                } else if (validRowsTemp.length === 0 && invalidRowsTemp.length > 0) {
                    setMessage(`Excel dosyası yüklendi ancak hiçbir geçerli satır bulunamadı.`)
                    setMessageType('warning')
                } else if (mappedData.length === 0) {
                    setMessage('Excel dosyası boş veya geçersiz format.')
                    setMessageType('error')
                } else {
                    setMessage('')
                }

                setShowNoValidRowsWarning(validRowsTemp.length === 0 && invalidRowsTemp.length > 0)
            }
            fileReader.readAsBinaryString(selectedFile)
        } else {
            setLoading(false)
            setMessage(`Dosya formatı ${fileFormat.join(', ')} olmalıdır.`)
            setMessageType('error')
        }
    }

    const resetUpload = () => {
        setFormData([])
        setValidRows([])
        setInvalidRows([])
        setIsShowSendMoneyButton(false)
        setLoading(false)
        setIsScheduled(false)
        setScheduledDate(null)
        setMessage('')
        setShowNoValidRowsWarning(false)
        setShowConfirmModal(false)
        setIsConfirmLoading(false)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const sendMoneyRequest = () => {
        if (validRows.length > 0) {
            setShowConfirmModal(true)
        } else {
            setMessage('Gönderilecek doğru satır bulunamadı.')
            setMessageType('error')
        }
    }

    const handleConfirmSend = async () => {
        if (!currentUserState?.customerId || !currentUserState?.companyId) {
            enqueueSnackbar('Kullanıcı bilgileri bulunamadı', { variant: 'error' })
            return
        }

        if (validRows.length === 0) {
            enqueueSnackbar('Gönderilecek geçerli satır bulunamadı', { variant: 'error' })
            return
        }

        setIsConfirmLoading(true)

        try {
            // Excel'den gelen veriyi API formatına çevir
            const mealAllowanceData = validRows.map(row => ({
                description: row.description || '',
                receiverCardNo: encrypt(row.receiverCardNo), // Kart numarasını şifrele
                currencyId: getCurrency(row.currency), // Para birimi ID'sine çevir
                value: parseFloat(String(row.value)) || 0
            }))

            const payload = {
                data: mealAllowanceData,
                companyId: currentUserState.companyId,
                customerId: currentUserState.customerId,
                scheduledAt: isScheduled && scheduledDate ? scheduledDate.format('YYYY-MM-DDTHH:mm:ss') : null
            }

            const result = await sendMealAllowance(payload)

            if (result.data?.success) {
                enqueueSnackbar(result.data.message || 'Yemek yardımı başarıyla gönderildi', {
                    variant: 'success'
                })
                setShowConfirmModal(false)
                // Formu temizle
                resetUpload()
                // Parent modal'ı kapat
                onClose?.()
            } else {
                throw new Error(result.data?.message || 'İşlem başarısız')
            }
        } catch (error: any) {
            const errorMessage = error?.data?.message || error?.message || 'Yemek yardımı gönderilirken hata oluştu'
            enqueueSnackbar(errorMessage, { variant: 'error' })
        } finally {
            setIsConfirmLoading(false)
        }
    }

    const handleCancelSend = () => {
        setShowConfirmModal(false)
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <RestaurantMenu sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a202c' }}>
                            Yemek Kartı Bakiye Yükleme
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#64748b' }}>
                        Excel dosyası yükleyerek toplu olarak yemek kartlarına bakiye yükleyebilirsiniz
                    </Typography>
                </Paper>

                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {/* File Upload Section */}
                {!isShowSendMoneyButton && (
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* Download Sample Excel */}
                        <Grid item xs={12} md={4}>
                            <Card
                                sx={{
                                    height: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    border: '2px dashed #e0e7ff',
                                    '&:hover': {
                                        borderColor: '#3b82f6',
                                        bgcolor: 'rgba(59, 130, 246, 0.05)'
                                    }
                                }}
                                onClick={downloadSampleFile}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <CloudDownload sx={{ fontSize: 60, color: '#64748b', mb: 2 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        Örnek Excel Dosyasını İndiriniz
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Upload Excel */}
                        <Grid item xs={12} md={8}>
                            <Card
                                sx={{
                                    height: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    border: '2px dashed #e0e7ff',
                                    '&:hover': {
                                        borderColor: '#3b82f6',
                                        bgcolor: 'rgba(59, 130, 246, 0.05)'
                                    }
                                }}
                                onClick={() => {
                                    // Dosya input'unu temizle ki aynı dosya tekrar seçilebilsin
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = ''
                                    }
                                    fileInputRef.current?.click()
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <CloudUpload sx={{ fontSize: 60, color: '#64748b', mb: 2 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                        Excel Yükle
                                    </Typography>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={onFileChange}
                                        onClick={(e) => {
                                            // Input'a tıklandığında değeri temizle
                                            e.currentTarget.value = ''
                                        }}
                                        style={{ display: 'none' }}
                                        accept={fileFormat.join(',')}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Message Display */}
                {message && (
                    <Alert severity={messageType} sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }} >
                            {message}
                        </Typography>
                    </Alert>
                )}

                {/* Invalid Rows Table */}
                {isShowSendMoneyButton && invalidRows.length > 0 && (
                    <Paper sx={{ mb: 3 }}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Hatalı Satırlar
                            </Typography>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Refresh />}
                                onClick={resetUpload}
                            >
                                Yeniden Yükle
                            </Button>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Hatalı Satır İndeksi</TableCell>
                                        <TableCell>Kart Numarası</TableCell>
                                        <TableCell>Para Birimi</TableCell>
                                        <TableCell>Tutar</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invalidRows.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{row.__rowNum__}</TableCell>
                                            <TableCell sx={{ color: getCellClassAndText(row.receiverCardNo, 'receiverCardNo').class === 'text-green-800 font-bold' ? 'green' : 'red' }}>
                                                {getCellClassAndText(row.receiverCardNo, 'receiverCardNo').text}
                                            </TableCell>
                                            <TableCell sx={{ color: getCellClassAndText(row.currency, 'currency').class === 'text-green-800 font-bold' ? 'green' : 'red' }}>
                                                {getCellClassAndText(row.currency, 'currency').text}
                                            </TableCell>
                                            <TableCell sx={{ color: getCellClassAndText(row.value, 'value').class === 'text-green-800 font-bold' ? 'green' : 'red' }}>
                                                {getCellClassAndText(row.value, 'value').text}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Success Message - Sadece geçerli satır varsa ve hatalı satır yoksa göster 
                {validRows.length > 0 && invalidRows.length === 0 && isShowSendMoneyButton && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                            ✅ Mükemmel! Tüm satırlar başarıyla doğrulandı.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {validRows.length} satırın tümü geçerli ve işlenmeye hazır.
                        </Typography>
                    </Alert>
                )}*/}

                {/* Warning Message - Hatalı satır varsa göster */}
                {invalidRows.length > 0 && isShowSendMoneyButton && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                            ❌ Hatalı Kayıtlar Tespit Edildi!
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Excel dosyanızda <strong>{invalidRows.length}</strong> hatalı satır bulundu. 
                            Hatalı kayıtlar düzeltilmeden işlem başlatılamaz. 
                            Lütfen hataları düzeltin ve dosyayı yeniden yükleyin.
                        </Typography>
                    </Alert>
                )}

                {/* Valid Rows Table - Sadece hatalı kayıt yoksa göster */}
                {isShowSendMoneyButton && invalidRows.length === 0 && (
                    <Paper sx={{ mb: 3 }}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                İşlemler
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Doğru Satır İndeksi</TableCell>
                                        <TableCell>Kart Numarası</TableCell>
                                        <TableCell>Tutar</TableCell>
                                        <TableCell>Para Birimi</TableCell>
                                        <TableCell>Açıklama</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {validRows.map((data, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>{data.__rowNum__}</TableCell>
                                            <TableCell>{data.receiverCardNo}</TableCell>
                                            <TableCell>{formatCurrency(data.value.toString(), getCurrency(data.currency))}</TableCell>
                                            <TableCell>{data.currency}</TableCell>
                                            <TableCell>{data.description}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Scheduler Section - Sadece hatalı kayıt yoksa göster */}
                {formData.length > 0 && isShowSendMoneyButton && invalidRows.length === 0 && (
                    <Paper sx={{ mb: 3 }}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                                <Schedule sx={{ mr: 1 }} />
                                Zamanlayıcı Ayarları
                            </Typography>
                        </Box>
                        <CardContent>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isScheduled}
                                        onChange={(e) => setIsScheduled(e.target.checked)}
                                    />
                                }
                                label="Ödemeyi İleri Bir Tarihe Planla"
                                sx={{ mb: 2 }}
                            />

                            {isScheduled && (
                                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                                        Planlanan Ödeme Tarihi
                                    </Typography>
                                    <DateTimePicker
                                        label="Tarih ve Saat Seçiniz"
                                        value={scheduledDate}
                                        onChange={(newValue) => setScheduledDate(newValue)}
                                        format="DD/MM/YYYY HH:mm"
                                        slotProps={{
                                            textField: {
                                                size: 'medium',
                                                fullWidth: true,
                                                sx: { maxWidth: 300 }
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                background: 'white',
                                                '&:hover': {
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'primary.main',
                                                    },
                                                },
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                        </CardContent>
                    </Paper>
                )}

                {/* Error Message - Sadece hatalı satırlar varsa ve geçerli satır yoksa göster */}
                {showNoValidRowsWarning && validRows.length === 0 && invalidRows.length > 0 && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                            ❌ Kritik Hata! Hiçbir satır işlenemiyor.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Toplam <strong>{invalidRows.length}</strong> hatalı satır tespit edildi.
                            Lütfen Excel dosyanızdaki verileri kontrol edin ve yeniden yükleyin.
                        </Typography>
                    </Alert>
                )}

                {/* Send Money Button - Sadece hatalı kayıt yoksa göster */}
                {isShowSendMoneyButton && validRows.length > 0 && invalidRows.length === 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Send />}
                            onClick={sendMoneyRequest}
                            sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                }
                            }}
                        >
                            Para Gönderme İşlemini Başlat ({validRows.length} satır)
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={handleCancelSend}
                onConfirm={handleConfirmSend}
                title="Bakiye Yükleme Onayı"
                content={`${validRows.length} adet karta bakiye yüklemek istediğinizden emin misiniz?`}
                confirmButtonText="Onayla ve Başlat"
                closeButtonText="İptal"
                isLoading={isConfirmLoading || apiLoading}
                optionalWidth="400px"
            >
                {isScheduled && scheduledDate && (
                    <Alert severity="info" sx={{ mt: 2, mx: 2 }}>
                        <Typography variant="body2">
                            <strong>Planlanan Tarih:</strong> {scheduledDate.format('DD/MM/YYYY HH:mm')}
                        </Typography>
                    </Alert>
                )}
            </ConfirmationModal>
        </LocalizationProvider>
    )
}

export default LoadMealBalance
